import React, { useState } from "react";
import { motion } from "framer-motion"; // For animations
import {
  FaEnvelope,
  FaMapMarkerAlt,
  FaGithub,
  FaLinkedinIn,
  FaInstagram,
} from "react-icons/fa"; // Icons from react-icons
import { FaXTwitter } from "react-icons/fa6";

function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [successMessageVisible, setSuccessMessageVisible] = useState(false);
  const [status, setStatus] = useState("");
  const [errors, setErrors] = useState({ name: "", email: "", message: "" }); // For real-time validation
  const [isSubmitting, setIsSubmitting] = useState(false); // For submission state

  // Real-time validation and character limits
  const validateInput = (field, value) => {
    switch (field) {
      case "name":
        return value.trim().length < 2
          ? "Name must be at least 2 characters long"
          : "";
      case "email":
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return !emailRegex.test(value)
          ? "Please enter a valid email address"
          : "";
      case "message":
        return value.length > 500
          ? "Message must not exceed 500 characters"
          : value.trim().length < 10
          ? "Message must be at least 10 characters long"
          : "";
      default:
        return "";
    }
  };

  const handleInputChange = (e, field) => {
    const value = e.target.value;
    const error = validateInput(field, value);
    setErrors((prev) => ({ ...prev, [field]: error }));

    switch (field) {
      case "name":
        setName(value);
        break;
      case "email":
        setEmail(value);
        break;
      case "message":
        setMessage(value);
        break;
      default:
        break;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus("Sending...");
    setSuccessMessageVisible(false);

    // Final validation before submission
    const nameError = validateInput("name", name);
    const emailError = validateInput("email", email);
    const messageError = validateInput("message", message);

    if (nameError || emailError || messageError) {
      setErrors({ name: nameError, email: emailError, message: messageError });
      setStatus("Please fix the errors before submitting.");
      setIsSubmitting(false);
      return;
    }

    try {
      // Simulate API call (replace with your actual endpoint)
      const response = await fetch("/api/send_email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ name, email, message }),
      });

      if (!response.ok) {
        console.error("API Error:", response.status, response.statusText);
        setStatus(
          `Failed to send message. Server responded with status ${response.status}.`
        );
        setIsSubmitting(false);
        return;
      }

      const result = await response.json();

      if (result.sent) {
        setStatus("Message sent successfully! I’ll get back to you soon.");
        setName("");
        setEmail("");
        setMessage("");
        setErrors({ name: "", email: "", message: "" });
        setSuccessMessageVisible(true);
        setTimeout(() => setSuccessMessageVisible(false), 10000); // Auto-hide after 10 seconds
      } else {
        setStatus("Failed to send message. Please try again.");
      }
    } catch (error) {
      console.error("Error in API call:", error);
      setStatus("An error occurred. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Framer Motion variants for animations
  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    in: { opacity: 1, y: 0 },
    out: { opacity: 0, y: -20 },
  };

  const pageTransition = {
    type: "tween",
    ease: "anticipate",
    duration: 0.7,
  };

  const inputVariants = {
    focus: { scale: 1.02, boxShadow: "0 0 8px rgba(16, 185, 129, 0.5)" }, // Emerald green shadow
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  return (
    <motion.section
      id="contact"
      initial="initial"
      animate="in"
      exit="out"
      variants={pageVariants}
      transition={pageTransition}
      className="min-h-screen bg-gray-900 py-16 px-4 sm:px-6 lg:px-8 relative overflow-hidden"
    >
      {/* Subtle Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900 opacity-95"></div>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;800&family=JetBrains+Mono:wght@400;700&display=swap');
          .map-container {
            border-radius: 8px;
            overflow: hidden;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          }
        `}
      </style>

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.h2
          initial={{ scale: 0.5 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.5 }}
          className="text-4xl md:text-6xl font-bold text-center text-emerald-400 font-['Inter'] mb-16 mt-12"
        >
          Let’s Connect
        </motion.h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          {/* Contact Form */}
          <motion.div
            variants={cardVariants}
            initial="hidden"
            animate="visible"
            transition={{ delay: 0.2 }}
            className="bg-gray-600/20 rounded-lg shadow-md p-8"
          >
            <form onSubmit={handleSubmit} className="space-y-8 justify-center">
              <motion.div variants={inputVariants} whileFocus="focus">
                <label
                  htmlFor="name"
                  className="block text-md font-medium text-gray-200 mb-2 font-['Inter']"
                >
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  value={name}
                  onChange={(e) => handleInputChange(e, "name")}
                  required
                  className={`w-full px-4 py-2 rounded-md border-2 ${
                    errors.name
                      ? "border-red-500 focus:border-red-500 focus:ring-red-200"
                      : "border-gray-700/30 focus:border-emerald-400 focus:ring-emerald-400/50"
                  } outline-none transition duration-300 bg-gray-900 text-gray-100 placeholder-gray-500`}
                  placeholder="Your name"
                />
                {errors.name && (
                  <p className="text-sm text-red-500 mt-1 font-['Inter']">
                    {errors.name}
                  </p>
                )}
              </motion.div>
              <motion.div variants={inputVariants} whileFocus="focus">
                <label
                  htmlFor="email"
                  className="block text-md font-medium text-gray-200 mb-2 font-['Inter']"
                >
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => handleInputChange(e, "email")}
                  required
                  className={`w-full px-4 py-2 rounded-md border-2 ${
                    errors.email
                      ? "border-red-500 focus:border-red-500 focus:ring-red-200"
                      : "border-gray-700/30 focus:border-emerald-400 focus:ring-emerald-400/50"
                  } outline-none transition duration-300 bg-gray-900 text-gray-100 placeholder-gray-500`}
                  placeholder="your.email@example.com"
                />
                {errors.email && (
                  <p className="text-sm text-red-500 mt-1 font-['Inter']">
                    {errors.email}
                  </p>
                )}
              </motion.div>
              <motion.div variants={inputVariants} whileFocus="focus">
                <label
                  htmlFor="message"
                  className="block text-md font-medium text-gray-200 mb-2 font-['Inter']"
                >
                  Message
                </label>
                <div className="relative">
                  <textarea
                    id="message"
                    rows="6"
                    value={message}
                    onChange={(e) => handleInputChange(e, "message")}
                    required
                    className={`w-full px-4 py-2 rounded-md border-2 ${
                      errors.message
                        ? "border-red-500 focus:border-red-500 focus:ring-red-200"
                        : "border-gray-700/30 focus:border-emerald-400 focus:ring-emerald-400/50"
                    } outline-none transition duration-300 resize-none bg-gray-900 text-gray-100 placeholder-gray-500`}
                    placeholder="Describe your opportunity or question..."
                  ></textarea>
                  <span className="absolute right-4 bottom-2 text-gray-500 text-xs font-['Inter']">
                    {message.length}/500
                  </span>
                </div>
                {errors.message && (
                  <p className="text-sm text-red-500 mt-1 font-['Inter']">
                    {errors.message}
                  </p>
                )}
              </motion.div>
              <motion.button
                type="submit"
                whileHover={{ scale: 1.07 }}
                whileTap={{ scale: 0.95 }}
                disabled={isSubmitting}
                className={`w-full py-3 px-4 text-lg font-semibold rounded-lg shadow-lg transition duration-300 ease-in-out 
    ${
      isSubmitting
        ? "bg-gray-700 text-gray-500 cursor-not-allowed"
        : "bg-gradient-to-r from-emerald-700 via-emerald-400 to-emerald-600 text-white hover:from-emerald-600 hover:via-emerald-300 hover:to-emerald-500 hover:shadow-xl"
    }`}
              >
                {isSubmitting ? "Sending..." : "Send Message"}
              </motion.button>
            </form>
            {status && (
              <p className="text-center text-sm font-medium text-gray-400 mt-4 font-['Inter']">
                {status}
              </p>
            )}
            {successMessageVisible && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.3 }}
                className="fixed top-4 right-4 bg-emerald-500/20 text-emerald-400 px-6 py-3 rounded-lg shadow-md border border-emerald-600"
              >
                🎉 Email sent successfully! I’ll get back to you soon.
              </motion.div>
            )}
          </motion.div>

          {/* Contact Info & Social Links */}
          <motion.div
            variants={cardVariants}
            initial="hidden"
            animate="visible"
            transition={{ delay: 0.4 }}
            className="bg-gray-600/20 rounded-lg shadow-md p-8"
          >
            <h4 className="text-2xl font-semibold mb-6 text-cyan-500 font-['Inter']">
              Get in Touch
            </h4>
            <div className="space-y-6">
              {/* Opportunity Info (Developer Looking for Software Roles) */}
              <motion.div
                className="flex items-center space-x-4"
                whileHover={{ x: 10 }}
                transition={{ duration: 0.3 }}
              >
                <FaEnvelope className="text-emerald-400 text-2xl" />
                <span className="text-gray-400 font-['Inter'] text-base">
                  I’m a{" "}
                  <span className="font-bold text-emerald-400">Developer</span>{" "}
                  seeking new{" "}
                  <span className="font-bold text-emerald-400">
                    Software Role opportunities
                  </span>
                  . Feel free to connect via the form!
                </span>
              </motion.div>

              {/* Location */}
              <motion.div
                className="flex items-center space-x-4"
                whileHover={{ x: 10 }}
                transition={{ duration: 0.3 }}
              >
                <FaMapMarkerAlt className="text-emerald-400 text-2xl" />
                <span className="text-gray-400 font-['Inter'] text-base">
                  I'm from Kolkata, India
                </span>
              </motion.div>

              {/* Social Media Links */}
              <h4 className="text-2xl font-semibold mb-4 text-gray-100 font-['Inter']">
                Connect with Me
              </h4>
              <div className="flex space-x-4">
                <motion.a
                  href="https://github.com/CodeTirtho97"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.2, rotate: 360 }}
                  transition={{ duration: 0.5 }}
                  className="text-gray-400 hover:text-emerald-400 transition-colors"
                >
                  <FaGithub size={24} />
                </motion.a>
                <motion.a
                  href="https://www.linkedin.com/in/tirthoraj-bhattacharya/"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.2, rotate: 360 }}
                  transition={{ duration: 0.5 }}
                  className="text-gray-400 hover:text-emerald-400 transition-colors"
                >
                  <FaLinkedinIn size={24} />
                </motion.a>
                <motion.a
                  href="https://x.com/lucifer_7951"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.2, rotate: 360 }}
                  transition={{ duration: 0.5 }}
                  className="text-gray-400 hover:text-emerald-400 transition-colors"
                >
                  <FaXTwitter size={24} />
                </motion.a>
                <motion.a
                  href="https://www.instagram.com/lucifer_9751/"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.2, rotate: 360 }}
                  transition={{ duration: 0.5 }}
                  className="text-gray-400 hover:text-emerald-400 transition-colors"
                >
                  <FaInstagram size={24} />
                </motion.a>
              </div>

              {/* Location Map */}
              <div className="mt-6">
                <h4 className="text-lg font-semibold mb-4 text-gray-100 font-['Inter'] flex items-center">
                  My Current Location{" "}
                  <FaMapMarkerAlt className="ml-2 text-emerald-400" />
                </h4>
                <div className="map-container rounded-lg overflow-hidden shadow-md">
                  <iframe
                    title="Google Map - Prayagraj, India"
                    src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d114537.29282548174!2d81.853669!3d25.435801!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x399ab61d2e9131d5%3A0xe34d2765cd7c2450!2sPrayagraj%2C%20Uttar%20Pradesh!5e0!3m2!1sen!2sin!4v1626825007977!5m2!1sen!2sin"
                    width="100%"
                    height="250"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    className="w-full h-full"
                  ></iframe>

                  <motion.a
                    href="https://www.google.com/maps?q=Prayagraj,+India"
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.05 }}
                    className="mt-2 inline-block text-emerald-400 hover:text-emerald-300 font-['Inter'] text-base"
                  >
                    View Larger Map
                  </motion.a>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
}

export default Contact;
